import React from 'react';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';
import { fg } from '@confluence/feature-gating';
import { LoadableBackground } from '@confluence/loadable';

// Keep the machine registry graph out of the root App bundle and off the page-load
// critical path. The host renders nothing and only observes flows that start from
// user interaction, so it loads in the background phase: no SSR preload and no UFO
// load hold on every page view. A late mount reads the registry's current snapshot.
// Producers call `preloadRemixWatcherHost` at flow admission so the chunk is already
// in flight before any machine-derived phase becomes user-visible.
const LazyRemixWatcherHost = LoadableBackground({
	name: 'RemixWatcherHost',
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-RemixWatcherHost" */ './RemixWatcherHost.tsx'))
			.RemixWatcherHost,
	loading: () => null,
});

/**
 * Start fetching the watcher host chunk ahead of the background phase. Call from a
 * flow-start path (renderer `openModal`, editor `startFlow`) so a flow that begins
 * before background loading has run does not wait on the chunk for its first
 * conversation-derived transition. No-op while the master gate is off.
 */
export const preloadRemixWatcherHost = (): void => {
	if (!fg('remix_state_rearchitecture_enabled')) {
		return;
	}
	// Fire-and-forget: the loadable surfaces load failures through its own error path.
	void LazyRemixWatcherHost.preload();
};

export const RemixWatcherMount = (): React.JSX.Element => (
	<TransparentErrorBoundary attribution={Attribution.CE_MAU}>
		{/* Keep surface experiments at producer admission to avoid global renderer/editor exposure. */}
		{fg('remix_state_rearchitecture_enabled') ? <LazyRemixWatcherHost /> : null}
	</TransparentErrorBoundary>
);
